/**
 * TeacherRecitationsReview — list every student recording for one
 * recitation-type assignment, play it inline, give a 0–100 score and a
 * comment. Mobile-friendly (cards, big play buttons).
 *
 * Route: /teacher/recitations/:assignmentId
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCcw, CheckCircle2, Clock, Loader2, Save, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { recitationApi, learningApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const headers = () => {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const TeacherRecitationsReviewPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [assignment, setAssignment] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, assignmentId]);

  const refresh = async () => {
    setLoading(true); setError('');
    try {
      const [a, r] = await Promise.all([
        learningApi.assignmentDetail(assignmentId),
        recitationApi.listForTeacher(assignmentId),
      ]);
      setAssignment(a.data);
      setRecordings(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement.");
    } finally { setLoading(false); }
  };

  if (authLoading || loading) return <PageLoading />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 lg:pt-28"
         data-testid="teacher-recitations-review">
      <Link to="/devoirs" className="inline-flex items-center text-text-secondary hover:text-text-primary mb-6 text-sm">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux devoirs
      </Link>

      <SectionHeader overline="Évaluation"
                     title={assignment?.title || 'Récitations'}
                     subtitle="Écoutez chaque enregistrement et attribuez une note." />

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-danger/15 border border-danger/30 text-danger text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end mt-3">
        <Button size="sm" variant="outline" onClick={refresh} data-testid="recitations-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
      </div>

      {recordings.length === 0 ? (
        <GlassCard className="mt-6 py-10 text-center text-sm text-text-secondary">
          Aucun enregistrement reçu pour l'instant.
        </GlassCard>
      ) : (
        <div className="space-y-3 mt-6" data-testid="recitations-list">
          {recordings.map((r) => (
            <RecitationRow key={r.id} rec={r} onGraded={refresh} />
          ))}
        </div>
      )}
    </div>
  );
};

const RecitationRow = ({ rec, onGraded }) => {
  const [score, setScore] = useState(rec.teacher_score ?? '');
  const [comment, setComment] = useState(rec.teacher_comment ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);

  // Fetch audio with auth header (Response is binary; use Blob)
  useEffect(() => {
    let cancelled = false;
    let url;
    fetch(recitationApi.audioUrl(rec.id), {
      headers: headers(), credentials: 'include',
    })
      .then((res) => res.ok ? res.blob() : Promise.reject(new Error(res.statusText)))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setAudioUrl(url);
      })
      .catch(() => setError("Audio indisponible."));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [rec.id]);

  const submit = async () => {
    const n = parseInt(score, 10);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      setError('Note invalide (0 à 100).');
      return;
    }
    setBusy(true); setError('');
    try {
      await recitationApi.grade(rec.id, { score: n, comment: comment.trim() || null });
      onGraded?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur d'enregistrement.");
    } finally { setBusy(false); }
  };

  return (
    <GlassCard className="p-4" data-testid={`recitation-row-${rec.id}`}>
      <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-text-primary font-medium truncate">
            {rec.student?.name || rec.student?.email || 'Élève'}
          </div>
          <p className="text-xs text-text-muted">
            Soumis le {new Date(rec.created_at).toLocaleString('fr-FR')}
            {rec.duration_sec ? ` · ${Math.round(rec.duration_sec)}s` : ''}
            {rec.size_bytes ? ` · ${Math.round(rec.size_bytes / 1024)} KB` : ''}
          </p>
        </div>
        {rec.status === 'graded' ? (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px]">
            <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Noté · {rec.teacher_score}/100
          </Badge>
        ) : (
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border text-[10px]">
            <Clock className="w-3 h-3 mr-1 inline" /> En attente
          </Badge>
        )}
      </div>

      {audioUrl ? (
        <audio src={audioUrl} controls className="w-full mb-3" data-testid={`recitation-audio-${rec.id}`} />
      ) : (
        <div className="text-xs text-text-muted mb-3">{error || 'Chargement de l\'audio…'}</div>
      )}

      <div className="flex flex-wrap gap-2 items-end">
        <div className="w-24">
          <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Note /100</label>
          <Input type="number" min={0} max={100} value={score}
                 onChange={(e) => setScore(e.target.value)}
                 className="bg-surface border-border"
                 data-testid={`recitation-score-${rec.id}`} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Commentaire (optionnel)</label>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                    className="bg-surface border-border min-h-[44px]" rows={2}
                    placeholder="Encourage, donne un conseil tajwid…"
                    data-testid={`recitation-comment-${rec.id}`} />
        </div>
        <Button onClick={submit} disabled={busy} className="bg-primary text-background hover:bg-primary-hover"
                data-testid={`recitation-grade-submit-${rec.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Enregistrer la note
        </Button>
      </div>
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </GlassCard>
  );
};

export default TeacherRecitationsReviewPage;
